import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { GoldButton, GhostButton } from "../components/Buttons";
import { MaskedLines, Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { VALUES, AUDIENCES, IMAGES, CONTACT, EXPERIENCE } from "../data/content";
import Seo from "../components/Seo";

export default function About() {
    return (
        <div data-testid="page-about">
            <Seo
                title="About Freedom Tax Filer | U.S. Tax Preparation Since 2017"
                description="Established in 2017, Freedom Tax Filer combines careful preparation, plain-language explanations, and personal service on every tax return we prepare."
                path="/about"
            />
            {/* Page hero */}
            <section className="relative pt-44 pb-20 sm:pb-24 overflow-hidden">
                <div className="absolute inset-0">
                    <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-25" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#111318]/70 via-[#111318]/85 to-[#111318]" />
                </div>
                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">About Freedom Tax Filer</p>
                        <div className="gold-rule mt-5" />
                    </Reveal>
                    <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-8 max-w-3xl">
                        <MaskedLines delay={0.2} lines={["More than", <>a tax <em className="italic text-gold-gradient">service.</em></>]} />
                    </h1>
                    <Reveal delay={0.5}>
                        <p className="text-white/70 text-lg leading-relaxed mt-8 max-w-2xl">
                            We built Freedom Tax Filer around a simple belief: the people paying taxes deserve preparation
                            that is careful, explained, and personal — whatever size their paycheck or business.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Story */}
            <section className="section-pad pt-4" data-testid="about-story">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    <Reveal>
                        <div className="frame-gold">
                            <img src={IMAGES.about} alt="Two professionals collaborating in a modern office" className="w-full h-[340px] sm:h-[460px] object-cover" loading="lazy" decoding="async" data-testid="about-image" />
                        </div>
                    </Reveal>
                    <div className="space-y-6">
                        <SectionHeading eyebrow="Who We Are" title="Clear taxes, considered work." />
                        <Reveal delay={0.1}>
                            <p className="text-[#4A4F58] leading-relaxed">
                                Freedom Tax Filer is a U.S. tax preparation service for everyday earners and the businesses
                                that employ them. We serve W-2 employees, working professionals, gig workers and freelancers,
                                multi-state filers, and small-business owners.
                            </p>
                            <p className="text-[#4A4F58] leading-relaxed">
                                Our approach is unhurried where it matters: understanding your situation before touching a
                                form, preparing every return line by line, and explaining the result in plain language. When
                                something applies to you, we tell you why. When it does not, we tell you that too.
                            </p>
                            <p className="text-[#4A4F58] leading-relaxed" data-testid="about-experience-paragraph">
                                {EXPERIENCE.aboutParagraph}
                            </p>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <p className="text-[#737985] text-sm uppercase tracking-[0.2em]">Who we serve</p>
                            <div className="flex flex-wrap gap-3 mt-4">
                                {AUDIENCES.map((a) => (
                                    <span key={a} className="text-xs uppercase tracking-[0.14em] text-[#4A4F58] border border-[#D9D6CD] bg-white px-4 py-2">
                                        {a}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="section-pad bg-[#F3F1EC] border-y border-[#E7E4DC]" data-testid="about-values">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <SectionHeading eyebrow="How We Work" title="Four commitments behind every return." />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
                        {VALUES.map((v, i) => {
                            const Icon = v.icon;
                            return (
                                <Reveal key={v.title} delay={i * 0.08}>
                                    <div className="h-full bg-white border border-[#E7E4DC] shadow-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#C8A45D]/60 hover:shadow-md" data-testid={`about-value-card-${i}`}>
                                        <div className="h-12 w-12 rounded-lg border border-[#C8A45D]/50 flex items-center justify-center">
                                            <Icon className="h-5 w-5 text-[#8A6D2F]" />
                                        </div>
                                        <h3 className="font-display text-xl text-[#20242B] mt-7">{v.title}</h3>
                                        <p className="text-[#737985] text-sm leading-relaxed mt-3">{v.text}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-pad bg-[#111318]" data-testid="about-cta">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 text-left">
                    <Reveal>
                        <h2 className="font-display text-3xl sm:text-5xl text-white leading-tight max-w-2xl">
                            Let's find out what your situation <em className="italic text-gold-gradient">actually needs.</em>
                        </h2>
                        <p className="text-white/60 leading-relaxed mt-5 max-w-xl">
                            A short conversation is the fastest way to know. Reach out, tell us a little about yourself, and
                            we will map the next steps.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 mt-10">
                            <GoldButton asChild data-testid="about-cta-get-started">
                                <Link to="/contact">
                                    Get Started <ArrowRight className="h-4 w-4" />
                                </Link>
                            </GoldButton>
                            {CONTACT.phoneHref && (
                                <GhostButton asChild data-testid="about-cta-call">
                                    <a href={CONTACT.phoneHref}>
                                        <Phone className="h-4 w-4" /> Call Us
                                    </a>
                                </GhostButton>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
